import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/**
 * Usage against each plan's quotas — billing infrastructure, counted live (Q-1). A `null` count
 * is a quota nothing measures yet, which is not the same as none used.
 */

const usageRowSchema = z.object({
  tenantId: z.uuid(),
  tenantSlug: z.string(),
  tenantName: z.string(),
  status: z.string(),
  planKey: z.string(),
  planName: z.string(),
  companies: z.number(),
  eventProfiles: z.number(),
  outboxPending: z.number(),
  usage: z.record(z.string(), z.number().nullable()),
  quotas: z.record(z.string(), z.number()),
})

export type UsageRow = z.infer<typeof usageRowSchema>

export function fetchUsage(tenantId?: string, signal?: AbortSignal) {
  return api.get(`/v1/platform/usage${tenantId === undefined ? '' : `?tenantId=${tenantId}`}`, {
    schema: z.object({ items: z.array(usageRowSchema) }),
    ...(signal ? { signal } : {}),
  })
}

export const usageQuery = (tenantId?: string) =>
  queryOptions({
    queryKey: ['platform', 'usage', tenantId ?? 'all'],
    queryFn: ({ signal }) => fetchUsage(tenantId, signal),
    staleTime: 30_000,
  })

/** How full a quota is, as the meter shows it. */
export function quotaState(used: number | null, limit: number | undefined) {
  if (used === null) return { tone: 'unknown' as const, ratio: 0 }
  if (limit === undefined) return { tone: 'unlimited' as const, ratio: 0 }
  if (limit === 0) return { tone: used > 0 ? ('over' as const) : ('ok' as const), ratio: 1 }
  const ratio = used / limit
  return {
    tone: ratio >= 1 ? ('over' as const) : ratio >= 0.8 ? ('near' as const) : ('ok' as const),
    ratio,
  }
}
